import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TroopInput = () => {
  const [troopData, setTroopData] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [troopDumps, setTroopDumps] = useState([]);

  useEffect(() => {
    loadTroopDumps();
  }, []);

  const loadTroopDumps = async () => {
    try {
      const response = await axios.get('/api/troops/list');
      if (response.data?.data?.troopDumps && Array.isArray(response.data.data.troopDumps)) {
        setTroopDumps(response.data.data.troopDumps);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Truppen-Dumps:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!troopData.trim()) {
      setMessage('Bitte Truppen-Daten eingeben');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post('/api/troops/submit', { troopData });
      setMessage(response.data?.message || 'Truppen-Daten erfolgreich gespeichert');
      setMessageType('success');
      setTroopData('');
      loadTroopDumps(); // Aktualisiere die Liste
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Speichern der Truppen-Daten';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTroopDump = async (dumpId) => {
    try {
      const response = await axios.delete(`/api/troops/${dumpId}`);
      setMessage(response.data?.message || 'Truppen-Dump gelöscht');
      setMessageType('success');
      loadTroopDumps();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen des Truppen-Dumps';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('de-DE');
    } catch {
      return 'Unbekannt';
    }
  };

  const formatCoords = (coords) => {
    if (coords && typeof coords === 'object' && coords.x && coords.y) {
      return `${coords.x}|${coords.y}`;
    }
    return 'Unbekannt';
  };

  const getTotalUnits = (villages) => {
    if (!Array.isArray(villages)) return 0;
    return villages.reduce((total, village) => {
      if (village.units) {
        return total + Object.values(village.units).reduce((sum, count) => sum + (count || 0), 0);
      }
      return total;
    }, 0);
  };

  const unitLabels = {
    spear: '🔱',      // Speer - Dreizack
    sword: '⚔️',      // Schwert - gekreuzte Schwerter 
    axe: '🪓',        // Axt - Axt-Icon
    archer: '🏹',     // Bogenschütze - Pfeil und Bogen
    spy: '🐎',        // Späher - weißes Pferd
    light: '🐴',      // Leichte Kavallerie - braunes Pferd 
    marcher: '🏇',    // Berittene Bogenschützen - Reiter
    heavy: '⚫🐎',    // Schwere Kavallerie - schwarzes Pferd
    ram: '🐏',        // Ramme - Widder (selbsterklärend)
    catapult: '💥',   // Katapult - Explosion (selbsterklärend)
    knight: '👑',     // Paladin - Krone
    snob: '🏰'        // Adelsgeschlecht - Burg
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full shadow-lg mb-4">
            <span className="text-2xl text-white">⚔️</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Truppen-Eingabe
          </h1>
          <p className="text-xl text-gray-600">Armeeanalyse für optimale Verteidigung</p>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-lg ${
            messageType === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {message}
          </div>
        )}

        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-3">
                🏰 Truppen-Daten aus dem Spiel
              </label>
              <div className="text-sm text-gray-600 mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-medium text-blue-800 mb-2">💡 Anleitung:</p>
                <p className="mb-2">1. Im Spiel: Gehe zu Truppen → Übersicht</p>
                <p className="mb-2">2. Kopiere die Tabelle (Strg+A, dann Strg+C)</p>
                <p className="mb-2">3. Füge die Daten hier ein (Strg+V)</p>
                <p className="text-xs text-blue-700 mt-3">
                  <strong>Beispiel-Format:</strong><br />
                  Speerträger: 5000<br />
                  Schwertkämpfer: 3000<br />
                  Axtträger: 2000<br />
                  ...oder BB-Code Format
                </p>
              </div>
              <textarea
                value={troopData}
                onChange={(e) => setTroopData(e.target.value)}
                className="w-full h-40 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                placeholder={`Reihenfolge: Speer, Schwert, Axt, Bogen, Späher, LKav, Berittene, SKav, Ramme, Kata

Beispiel BB-Code:
[table]
[**] Dorf [||] Speer [||] Schwert [||] Axt [||] Bogen [||] Späher [||] LKav [||] Berittene [||] SKav [||] Ramme [||] Kata [||] Paladin [||] Adelsgeschlecht [/**]
[*] Dorf1 (123|456) [|] 5000 [|] 3000 [|] 2000 [|] 1000 [|] 500 [|] 400 [|] 300 [|] 200 [|] 10 [|] 5 [|] 0 [|] 0 [/*]
[/table]`}
                disabled={loading}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Verarbeitung...</span>
                </>
              ) : (
                <>
                  <span>📊</span>
                  <span>Truppen analysieren</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Existing Troop Dumps */}
        {troopDumps.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="mr-3">📋</span>
              Gespeicherte Truppen-Analysen
            </h2>
            
            <div className="space-y-4">
              {troopDumps.map((dump) => (
                <div key={dump._id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="text-lg font-semibold text-gray-800 mb-1">
                        📊 Analyse vom {formatDate(dump.timestamp)}
                      </div>
                      <div className="text-sm text-gray-600">
                        🏘️ {dump.villages?.length || 0} Dörfer • 
                        ⚔️ {getTotalUnits(dump.villages)} Gesamteinheiten
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteTroopDump(dump._id)}
                      className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-100 transition-colors"
                      title="Löschen"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  {/* Villages Overview */}
                  {dump.villages && dump.villages.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700">🏰 Dörfer:</h4>
                      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {dump.villages.slice(0, 6).map((village, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-3 border">
                            <div className="font-medium text-gray-800 mb-2">
                              {village.villageName || `Dorf ${index + 1}`}
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              📍 {formatCoords(village.coords)}
                            </div>
                            {village.units && (
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(village.units)
                                  .filter(([_, count]) => count > 0)
                                  .slice(0, 4)
                                  .map(([unit, count]) => (
                                    <span key={unit} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      {unitLabels[unit] || unit} {count}
                                    </span>
                                  ))}
                                {Object.entries(village.units).filter(([_, count]) => count > 0).length > 4 && (
                                  <span className="text-xs text-gray-500">+{Object.entries(village.units).filter(([_, count]) => count > 0).length - 4} mehr</span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        {dump.villages.length > 6 && (
                          <div className="bg-gray-100 rounded-lg p-3 border flex items-center justify-center text-gray-600">
                            +{dump.villages.length - 6} weitere Dörfer
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TroopInput;
